import { useEffect, useMemo } from "react";
import { CanvasStack } from "./CanvasStack.tsx";
import {
  type CanvasDismissKind,
  CanvasDismissProvider,
  useCanvasActionContext,
} from "./canvasContext.tsx";
import { parseCanvasContent } from "./canvasContent.ts";

/** Stable no-op for the clear/pop fallback when there's no canvas action context
 *  (home, gate-off), so a fresh `() => {}` doesn't defeat memoization downstream. */
const NOOP = () => {};

/** Stable empty stack, so an absent `canvasStack` prop keeps the same reference
 *  across renders and doesn't recompute the `layers` memo every render. */
const EMPTY_STACK: string[] = [];

/**
 * The canvas modal stack plus the bookkeeping every host of it needs: parse each
 * stored token, heal the ones that no longer resolve, and wire dismissal to the
 * canvas action context.
 *
 * Split out from {@link CanvasHost} so a surface can present canvas cards over
 * its own content with no calendar underneath — the participants page does that,
 * where a calendar would be unrelated to anything on screen.
 *
 * The stack's layer wrappers are `absolute inset-0`, so the region a card covers
 * and dims is decided by the nearest positioned ancestor the host supplies, not
 * here.
 */
export const CanvasLayers = ({
  canvasStack = EMPTY_STACK,
  isCanvasOpen = true,
  isFullscreen = false,
  dismiss = "close",
}: {
  /** The canvas modal stack (empty renders nothing). A plain render input — the
   *  hosting surface reads the canvas state and passes it in. */
  canvasStack?: string[];
  /** Whether the pane is expanded — gates focus restoration into a revealed card. */
  isCanvasOpen?: boolean;
  /** Sticky display mode of the presented entity, forwarded to each card. */
  isFullscreen?: boolean;
  /**
   * What dismissing a card does here — see {@link CanvasDismissKind}. A host
   * whose column exists only to hold these cards passes `collapse`, and their
   * close control becomes the column's own.
   */
  dismiss?: CanvasDismissKind;
}) => {
  // Pair each stored token with its parse so an unparseable one can be dropped
  // by URN below — `parseCanvasContent` discards the string it read.
  const parsed = useMemo(
    () => canvasStack.map((urn) => ({ urn, layer: parseCanvasContent(urn) })),
    [canvasStack],
  );
  // A layer the parser rejects has no card to render, but its wrapper is still
  // `absolute inset-0` and — when it is the top — neither `invisible` nor
  // `inert`, so it would sit over the host as an invisible click-blocker.
  // Only `CanvasArtifactModal` calls `onUnloadable`, and an unparseable entry
  // never mounts one, so nothing else would ever clear it.
  const layers = useMemo(
    () => parsed.filter((entry) => entry.layer !== null).map((entry) => entry.layer),
    [parsed],
  );
  const unparseable = useMemo(
    () => parsed.filter((entry) => entry.layer === null).map((entry) => entry.urn),
    [parsed],
  );

  const actions = useCanvasActionContext();
  const pop = actions?.pop ?? NOOP;
  const clear = actions?.clear ?? NOOP;
  const dropContent = actions?.dropContent ?? NOOP;

  // Heal the persisted stack, not just this render. Keyed on the joined value
  // rather than the array so a re-created `canvasStack` prop can't re-fire the
  // PATCH; once dropped the entry is gone from the stack and this settles empty.
  const unparseableKey = unparseable.join("\n");
  useEffect(() => {
    if (!unparseableKey) return;
    for (const urn of unparseableKey.split("\n")) dropContent(urn);
  }, [unparseableKey, dropContent]);

  return (
    // Through the React tree, so it reaches each card inside its portal.
    <CanvasDismissProvider value={dismiss}>
      <CanvasStack
        layers={layers}
        isCanvasOpen={isCanvasOpen}
        isFullscreen={isFullscreen}
        onPop={pop}
        onClear={clear}
        onDropContent={dropContent}
      />
    </CanvasDismissProvider>
  );
};
